import { css } from 'aphrodite';
import * as React from 'react';
import { IRichContentEditorState } from '../../../viewmodels/AppViewModels';
import { RichContentDocumentEditor } from '../../../web/components/richContent/RichContentDocumentEditor';
import { Button } from '../../../web/components-v2/Button';
import { titles } from '../../../web/styles/colors';
import { ConfirmationTab } from '../../hooks/useMeetingNoteRecording';
import { styleSheet } from './styles';

const ContentTeamEditorCSS = `
	body {
		background: transparent;
		color: ${titles};
		font-family: "Sora", sans-serif;
		font-size: 14px;
		margin: 0;
		padding: 16px 20px;
	}
`;

const RecapEmailEditorCSS = `
	body {
		background: transparent;
		color: ${titles};
		font-family: Arial, sans-serif;
		font-size: 14px;
		margin: 0;
		padding: 16px 20px;
	}
`;

interface IProps {
	didSkipMeetingNote: boolean;
	// Tab flow
	activeConfirmationTab: ConfirmationTab | null;
	completedTabs: Set<ConfirmationTab>;
	onTabChange: (tab: ConfirmationTab) => void;
	// Web Team
	showWebTeamConfirmation: boolean;
	webTeamSummary: string | null;
	isGeneratingWebTeamSummary: boolean;
	webTeamBusinessType: string;
	webTeamLocation: string;
	webTeamWebsiteUrl: string;
	onWebTeamBusinessTypeChange: (value: string) => void;
	onWebTeamLocationChange: (value: string) => void;
	onWebTeamWebsiteUrlChange: (value: string) => void;
	onGenerateWebTeamSummary: () => void;
	onSkipWebTeamSummary: () => void;
	onCopyWebTeamSummary: () => void;
	// Content Team
	showContentTeamConfirmation: boolean;
	contentTeamSummary: string | null;
	isGeneratingContentTeamSummary: boolean;
	contentTeamWebsiteUrl: string;
	contentTeamContentState: IRichContentEditorState | null;
	isSubmittingContentTeamNote: boolean;
	onContentTeamWebsiteUrlChange: (value: string) => void;
	onContentTeamContentStateChange: (state: IRichContentEditorState | null) => void;
	onGenerateContentTeamSummary: () => void;
	onSkipContentTeamSummary: () => void;
	onSubmitContentTeamNote: () => void;
	// Account Profile
	showAccountProfileConfirmation: boolean;
	accountProfileWebsiteUrl: string;
	isGeneratingAccountProfile: boolean;
	generatedBusinessDescription: string;
	generatedKeywords: string;
	generatedContentGoals: string;
	generatedLocation: string;
	generatedAddress: string;
	generatedPhoneNumber: string;
	generatedPronouns: string;
	generatedTonePreference: string;
	isSavingAccountProfile: boolean;
	onAccountProfileWebsiteUrlChange: (value: string) => void;
	onBusinessDescriptionChange: (value: string) => void;
	onKeywordsChange: (value: string) => void;
	onContentGoalsChange: (value: string) => void;
	onLocationChange: (value: string) => void;
	onAddressChange: (value: string) => void;
	onPhoneNumberChange: (value: string) => void;
	onPronounsChange: (value: string) => void;
	onTonePreferenceChange: (value: string) => void;
	onGenerateAccountProfile: () => void;
	onSaveAccountProfile: () => void;
	onSkipAccountProfile: () => void;
	// Recap Email
	showRecapEmailConfirmation: boolean;
	recapEmailContentState: IRichContentEditorState | null;
	recapEmailSubject: string;
	recapEmailRecipients: string;
	isGeneratingRecapEmail: boolean;
	isSendingRecapEmail: boolean;
	onRecapEmailContentStateChange: (state: IRichContentEditorState | null) => void;
	onRecapEmailSubjectChange: (value: string) => void;
	onRecapEmailRecipientsChange: (value: string) => void;
	onGenerateRecapEmail: () => void;
	onSendRecapEmail: () => void;
	onSkipRecapEmail: () => void;
}

export const PostMeetingConfirmations: React.FC<IProps> = ({
	didSkipMeetingNote,
	activeConfirmationTab,
	completedTabs,
	onTabChange,
	// Web Team
	showWebTeamConfirmation,
	webTeamSummary,
	isGeneratingWebTeamSummary,
	webTeamBusinessType,
	webTeamLocation,
	webTeamWebsiteUrl,
	onWebTeamBusinessTypeChange,
	onWebTeamLocationChange,
	onWebTeamWebsiteUrlChange,
	onGenerateWebTeamSummary,
	onSkipWebTeamSummary,
	onCopyWebTeamSummary,
	// Content Team
	showContentTeamConfirmation,
	contentTeamSummary,
	isGeneratingContentTeamSummary,
	contentTeamWebsiteUrl,
	contentTeamContentState,
	isSubmittingContentTeamNote,
	onContentTeamWebsiteUrlChange,
	onContentTeamContentStateChange,
	onGenerateContentTeamSummary,
	onSkipContentTeamSummary,
	onSubmitContentTeamNote,
	// Account Profile
	showAccountProfileConfirmation,
	accountProfileWebsiteUrl,
	isGeneratingAccountProfile,
	generatedBusinessDescription,
	generatedKeywords,
	generatedContentGoals,
	generatedLocation,
	generatedAddress,
	generatedPhoneNumber,
	generatedPronouns,
	generatedTonePreference,
	isSavingAccountProfile,
	onAccountProfileWebsiteUrlChange,
	onBusinessDescriptionChange,
	onKeywordsChange,
	onContentGoalsChange,
	onLocationChange,
	onAddressChange,
	onPhoneNumberChange,
	onPronounsChange,
	onTonePreferenceChange,
	onGenerateAccountProfile,
	onSaveAccountProfile,
	onSkipAccountProfile,
	// Recap Email
	showRecapEmailConfirmation,
	recapEmailContentState,
	recapEmailSubject,
	recapEmailRecipients,
	isGeneratingRecapEmail,
	isSendingRecapEmail,
	onRecapEmailContentStateChange,
	onRecapEmailSubjectChange,
	onRecapEmailRecipientsChange,
	onGenerateRecapEmail,
	onSendRecapEmail,
	onSkipRecapEmail,
}) => {
	return (
		<div className={css(styleSheet.confirmationContainer)}>
			{/* Success message header */}
			<div className={css(styleSheet.confirmationMessage)}>
				<div className={css(styleSheet.confirmationTitle)}>
					{didSkipMeetingNote ? 'Post-Meeting Actions' : 'Meeting Note Saved!'}
				</div>
				<div className={css(styleSheet.confirmationText)}>
					Complete any of the optional tasks below, or close the dialog when done.
				</div>
			</div>

			{/* Tab Bar */}
			<div className={css(styleSheet.confirmationTabBar)}>
				{showWebTeamConfirmation && (
					<button
						className={css(
							styleSheet.confirmationTab,
							activeConfirmationTab === 'webTeam' && styleSheet.confirmationTabActive,
							completedTabs.has('webTeam') && styleSheet.confirmationTabCompleted
						)}
						onClick={() => onTabChange('webTeam')}
					>
						Web Team {completedTabs.has('webTeam') && '\u2713'}
					</button>
				)}
				{showContentTeamConfirmation && (
					<button
						className={css(
							styleSheet.confirmationTab,
							activeConfirmationTab === 'contentTeam' && styleSheet.confirmationTabActive,
							completedTabs.has('contentTeam') && styleSheet.confirmationTabCompleted
						)}
						onClick={() => onTabChange('contentTeam')}
					>
						Content Team {completedTabs.has('contentTeam') && '\u2713'}
					</button>
				)}
				{showAccountProfileConfirmation && (
					<button
						className={css(
							styleSheet.confirmationTab,
							activeConfirmationTab === 'accountProfile' && styleSheet.confirmationTabActive,
							completedTabs.has('accountProfile') && styleSheet.confirmationTabCompleted
						)}
						onClick={() => onTabChange('accountProfile')}
					>
						AI Custom Content {completedTabs.has('accountProfile') && '\u2713'}
					</button>
				)}
				{showRecapEmailConfirmation && (
					<button
						className={css(
							styleSheet.confirmationTab,
							activeConfirmationTab === 'recapEmail' && styleSheet.confirmationTabActive,
							completedTabs.has('recapEmail') && styleSheet.confirmationTabCompleted
						)}
						onClick={() => onTabChange('recapEmail')}
					>
						Recap Email {completedTabs.has('recapEmail') && '\u2713'}
					</button>
				)}
			</div>

			{/* Tab Content */}
			<div className={css(styleSheet.confirmationTabContent)}>
				{/* Web Team Tab */}
				{activeConfirmationTab === 'webTeam' && (
					<div className={css(styleSheet.confirmationColumn, styleSheet.confirmationColumnFull)}>
						{!webTeamSummary ? (
							<div className={css(styleSheet.confirmationPanel)}>
								<h3 className={css(styleSheet.confirmationPanelTitle)}>Web Team Intake</h3>
								<div className={css(styleSheet.deepResearchFields)}>
									<div>
										<label className={css(styleSheet.deepResearchFieldLabel)}>Business Type</label>
										<input
											type='text'
											className={css(styleSheet.deepResearchFieldInput)}
											value={webTeamBusinessType}
											onChange={e => onWebTeamBusinessTypeChange(e.target.value)}
											placeholder='e.g., Insurance Agency'
											disabled={isGeneratingWebTeamSummary}
										/>
									</div>
									<div>
										<label className={css(styleSheet.deepResearchFieldLabel)}>Location</label>
										<input
											type='text'
											className={css(styleSheet.deepResearchFieldInput)}
											value={webTeamLocation}
											onChange={e => onWebTeamLocationChange(e.target.value)}
											placeholder='e.g., Raleigh, NC'
											disabled={isGeneratingWebTeamSummary}
										/>
									</div>
									<div>
										<label className={css(styleSheet.deepResearchFieldLabel)}>Website URL</label>
										<input
											type='text'
											className={css(styleSheet.deepResearchFieldInput)}
											value={webTeamWebsiteUrl}
											onChange={e => onWebTeamWebsiteUrlChange(e.target.value)}
											placeholder='e.g., https://example.com'
											disabled={isGeneratingWebTeamSummary}
										/>
									</div>
								</div>
								<div className={css(styleSheet.confirmationActions)}>
									<Button variant='secondary' onClick={onSkipWebTeamSummary} disabled={isGeneratingWebTeamSummary}>
										Skip
									</Button>
									<Button
										variant='success'
										onClick={onGenerateWebTeamSummary}
										isLoading={isGeneratingWebTeamSummary}
										disabled={isGeneratingWebTeamSummary}
									>
										Generate Web Team Summary
									</Button>
								</div>
							</div>
						) : (
							<>
								<div className={css(styleSheet.summaryHeader)}>
									<h3>Web Team Intake Summary</h3>
									<Button variant='primary' onClick={onCopyWebTeamSummary}>
										Copy
									</Button>
								</div>
								<div className={css(styleSheet.summaryNote)}>
									{"We'll automatically save this note to the account along with a research report for the web team."}
								</div>
								<div className={css(styleSheet.summaryContent)}>{webTeamSummary}</div>
							</>
						)}
					</div>
				)}

				{/* Content Team Tab */}
				{activeConfirmationTab === 'contentTeam' && (
					<div className={css(styleSheet.confirmationColumn, styleSheet.confirmationColumnFull)}>
						{!contentTeamSummary ? (
							<div className={css(styleSheet.confirmationPanel)}>
								<h3 className={css(styleSheet.confirmationPanelTitle)}>Content Team Intake</h3>
								<div className={css(styleSheet.deepResearchFields)}>
									<div>
										<label className={css(styleSheet.deepResearchFieldLabel)}>Website URL</label>
										<input
											type='text'
											className={css(styleSheet.deepResearchFieldInput)}
											value={contentTeamWebsiteUrl}
											onChange={e => onContentTeamWebsiteUrlChange(e.target.value)}
											placeholder='e.g., https://example.com'
											disabled={isGeneratingContentTeamSummary}
										/>
									</div>
								</div>
								<div className={css(styleSheet.confirmationActions)}>
									<Button
										variant='secondary'
										onClick={onSkipContentTeamSummary}
										disabled={isGeneratingContentTeamSummary}
									>
										Skip
									</Button>
									<Button
										variant='success'
										onClick={onGenerateContentTeamSummary}
										isLoading={isGeneratingContentTeamSummary}
										disabled={isGeneratingContentTeamSummary || !contentTeamWebsiteUrl}
									>
										Generate Content Team Summary
									</Button>
								</div>
							</div>
						) : (
							<>
								<div className={css(styleSheet.summaryHeader)}>
									<h3>Content Team Intake Summary</h3>
								</div>
								<div className={css(styleSheet.summaryNote)}>
									Edit the content below, then click Submit to save to the account and send to ClickUp.
								</div>
								<div className={css(styleSheet.contentTeamEditorContainer)}>
									<RichContentDocumentEditor
										contentState={contentTeamContentState}
										onContentStateChanged={onContentTeamContentStateChange}
										readOnly={isSubmittingContentTeamNote}
										config={{
											autoresizeToFitContent: true,
											minHeight: 400,
											contentHorizontalPadding: 20,
											toolbar: 'undo redo | bold italic underline | bullist numlist',
											contentRawCss: ContentTeamEditorCSS,
										}}
									/>
								</div>
								<div className={css(styleSheet.formActions)}>
									<button
										className={css(styleSheet.saveButton)}
										onClick={onSubmitContentTeamNote}
										disabled={isSubmittingContentTeamNote || !contentTeamContentState?.hasContent()}
									>
										{isSubmittingContentTeamNote ? 'Submitting...' : 'Submit to ClickUp'}
									</button>
								</div>
							</>
						)}
					</div>
				)}

				{/* Account Profile Tab */}
				{activeConfirmationTab === 'accountProfile' && (
					<div className={css(styleSheet.confirmationColumn, styleSheet.confirmationColumnFull)}>
						{!generatedBusinessDescription ? (
							<div className={css(styleSheet.confirmationPanel)}>
								<h3 className={css(styleSheet.confirmationPanelTitle)}>AI Custom Content Preferences</h3>
								<div className={css(styleSheet.deepResearchFields)}>
									<div>
										<label className={css(styleSheet.deepResearchFieldLabel)}>Website URL</label>
										<input
											type='text'
											className={css(styleSheet.deepResearchFieldInput)}
											value={accountProfileWebsiteUrl}
											onChange={e => onAccountProfileWebsiteUrlChange(e.target.value)}
											placeholder='e.g., https://example.com'
											disabled={isGeneratingAccountProfile}
										/>
									</div>
								</div>
								<div className={css(styleSheet.confirmationActions)}>
									<Button variant='secondary' onClick={onSkipAccountProfile} disabled={isGeneratingAccountProfile}>
										Skip
									</Button>
									<Button
										variant='success'
										onClick={onGenerateAccountProfile}
										isLoading={isGeneratingAccountProfile}
										disabled={isGeneratingAccountProfile}
									>
										Generate Preferences
									</Button>
								</div>
							</div>
						) : (
							<>
								<div className={css(styleSheet.summaryHeader)}>
									<h3>AI Custom Content Preferences</h3>
								</div>
								<div className={css(styleSheet.summaryNote)}>
									Review and edit the generated preferences below, then click Save to store them.
								</div>
								<div className={css(styleSheet.accountProfileFields)}>
									<div className={css(styleSheet.accountProfileFieldGrid)}>
										<div className={css(styleSheet.accountProfileField)}>
											<label className={css(styleSheet.accountProfileFieldLabel)}>Location</label>
											<input
												className={css(styleSheet.accountProfileFieldInput)}
												value={generatedLocation}
												onChange={e => onLocationChange(e.target.value)}
												disabled={isSavingAccountProfile}
											/>
										</div>
										<div className={css(styleSheet.accountProfileField)}>
											<label className={css(styleSheet.accountProfileFieldLabel)}>Address</label>
											<input
												className={css(styleSheet.accountProfileFieldInput)}
												value={generatedAddress}
												onChange={e => onAddressChange(e.target.value)}
												disabled={isSavingAccountProfile}
											/>
										</div>
										<div className={css(styleSheet.accountProfileField)}>
											<label className={css(styleSheet.accountProfileFieldLabel)}>Phone Number</label>
											<input
												className={css(styleSheet.accountProfileFieldInput)}
												value={generatedPhoneNumber}
												onChange={e => onPhoneNumberChange(e.target.value)}
												disabled={isSavingAccountProfile}
											/>
										</div>
										<div className={css(styleSheet.accountProfileField)}>
											<label className={css(styleSheet.accountProfileFieldLabel)}>Pronouns</label>
											<input
												className={css(styleSheet.accountProfileFieldInput)}
												value={generatedPronouns}
												onChange={e => onPronounsChange(e.target.value)}
												disabled={isSavingAccountProfile}
											/>
										</div>
										<div className={css(styleSheet.accountProfileField)}>
											<label className={css(styleSheet.accountProfileFieldLabel)}>Tone Preference</label>
											<input
												className={css(styleSheet.accountProfileFieldInput)}
												value={generatedTonePreference}
												onChange={e => onTonePreferenceChange(e.target.value)}
												disabled={isSavingAccountProfile}
											/>
										</div>
									</div>
									<div className={css(styleSheet.accountProfileField)}>
										<label className={css(styleSheet.accountProfileFieldLabel)}>Business Description</label>
										<textarea
											className={css(styleSheet.accountProfileTextarea)}
											value={generatedBusinessDescription}
											onChange={e => onBusinessDescriptionChange(e.target.value)}
											rows={6}
											disabled={isSavingAccountProfile}
										/>
									</div>
									<div className={css(styleSheet.accountProfileField)}>
										<label className={css(styleSheet.accountProfileFieldLabel)}>Content Goals</label>
										<textarea
											className={css(styleSheet.accountProfileTextarea)}
											value={generatedContentGoals}
											onChange={e => onContentGoalsChange(e.target.value)}
											rows={2}
											disabled={isSavingAccountProfile}
										/>
									</div>
									<div className={css(styleSheet.accountProfileField)}>
										<label className={css(styleSheet.accountProfileFieldLabel)}>Keywords</label>
										<textarea
											className={css(styleSheet.accountProfileTextarea)}
											value={generatedKeywords}
											onChange={e => onKeywordsChange(e.target.value)}
											rows={2}
											disabled={isSavingAccountProfile}
										/>
									</div>
								</div>
								<div className={css(styleSheet.formActions)}>
									<button
										className={css(styleSheet.saveButton)}
										onClick={onSaveAccountProfile}
										disabled={isSavingAccountProfile || !generatedBusinessDescription}
									>
										{isSavingAccountProfile ? 'Saving...' : 'Save Preferences'}
									</button>
								</div>
							</>
						)}
					</div>
				)}

				{/* Recap Email Tab */}
				{activeConfirmationTab === 'recapEmail' && (
					<div className={css(styleSheet.confirmationColumn, styleSheet.confirmationColumnFull)}>
						{!recapEmailContentState ? (
							<div className={css(styleSheet.confirmationPanel)}>
								<h3 className={css(styleSheet.confirmationPanelTitle)}>Send Recap Email</h3>
								<div className={css(styleSheet.confirmationText, styleSheet.confirmationTextWithMargin)}>
									Generate a recap email to send to meeting attendees summarizing the discussion.
								</div>
								<div className={css(styleSheet.confirmationActions)}>
									<Button variant='secondary' onClick={onSkipRecapEmail} disabled={isGeneratingRecapEmail}>
										Skip
									</Button>
									<Button
										variant='success'
										onClick={onGenerateRecapEmail}
										isLoading={isGeneratingRecapEmail}
										disabled={isGeneratingRecapEmail}
									>
										Generate Recap Email
									</Button>
								</div>
							</div>
						) : (
							<>
								<div className={css(styleSheet.deepResearchFields)}>
									<div>
										<label className={css(styleSheet.deepResearchFieldLabel)}>To (comma or semicolon separated)</label>
										<input
											type='text'
											className={css(styleSheet.deepResearchFieldInput)}
											value={recapEmailRecipients}
											onChange={e => onRecapEmailRecipientsChange(e.target.value)}
											placeholder='email1@example.com, email2@example.com'
											disabled={isSendingRecapEmail}
										/>
									</div>
									<div>
										<label className={css(styleSheet.deepResearchFieldLabel)}>Subject</label>
										<input
											type='text'
											className={css(styleSheet.deepResearchFieldInput)}
											value={recapEmailSubject}
											onChange={e => onRecapEmailSubjectChange(e.target.value)}
											disabled={isSendingRecapEmail}
										/>
									</div>
								</div>
								<div className={css(styleSheet.recapEmailEditorContainer)}>
									<RichContentDocumentEditor
										contentState={recapEmailContentState}
										onContentStateChanged={onRecapEmailContentStateChange}
										readOnly={isSendingRecapEmail}
										config={{
											autoresizeToFitContent: true,
											minHeight: 300,
											contentHorizontalPadding: 20,
											toolbar: 'undo redo | bold italic underline | bullist numlist',
											contentRawCss: RecapEmailEditorCSS,
										}}
									/>
								</div>
								<div className={css(styleSheet.formActions)}>
									<button
										className={css(styleSheet.cancelButton)}
										onClick={onSkipRecapEmail}
										disabled={isSendingRecapEmail}
									>
										Skip
									</button>
									<button
										className={css(styleSheet.saveButton)}
										onClick={onSendRecapEmail}
										disabled={
											isSendingRecapEmail ||
											!recapEmailContentState?.hasContent() ||
											!recapEmailSubject ||
											!recapEmailRecipients
										}
									>
										{isSendingRecapEmail ? 'Sending...' : 'Send Email'}
									</button>
								</div>
							</>
						)}
					</div>
				)}
			</div>
		</div>
	);
};
